import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { XIcon, UsersIcon, CalendarIcon, ClockIcon } from 'lucide-react';
interface TableData {
  id: string;
  x: number;
  y: number;
  section: string;
  capacity: number;
  price: string;
}
const firstFloorTables: TableData[] = [{
  id: '101',
  x: 120,
  y: 280,
  section: 'Main Floor',
  capacity: 4,
  price: '$500 min'
}, {
  id: '102',
  x: 170,
  y: 310,
  section: 'Main Floor',
  capacity: 6,
  price: '$700 min'
}, {
  id: '103',
  x: 220,
  y: 280,
  section: 'Main Floor',
  capacity: 4,
  price: '$500 min'
}, {
  id: '104',
  x: 270,
  y: 310,
  section: 'Main Floor',
  capacity: 8,
  price: '$900 min'
}, {
  id: '105',
  x: 320,
  y: 280,
  section: 'Main Floor',
  capacity: 4,
  price: '$500 min'
}, {
  id: '106',
  x: 120,
  y: 190,
  section: 'VIP Section',
  capacity: 6,
  price: '$1,200 min'
}, {
  id: '107',
  x: 180,
  y: 190,
  section: 'VIP Section',
  capacity: 8,
  price: '$1,500 min'
}, {
  id: '108',
  x: 240,
  y: 190,
  section: 'VIP Section',
  capacity: 6,
  price: '$1,200 min'
}, {
  id: '109',
  x: 300,
  y: 190,
  section: 'VIP Section',
  capacity: 10,
  price: '$2,000 min'
}, {
  id: '110',
  x: 420,
  y: 150,
  section: 'Skybox Suite',
  capacity: 15,
  price: '$3,000 min'
}, {
  id: '111',
  x: 420,
  y: 200,
  section: 'Skybox Suite',
  capacity: 20,
  price: '$4,000 min'
}, {
  id: '112',
  x: 100,
  y: 380,
  section: 'Bar Lounge',
  capacity: 4,
  price: '$300 min'
}, {
  id: '113',
  x: 160,
  y: 380,
  section: 'Bar Lounge',
  capacity: 4,
  price: '$300 min'
}, {
  id: '114',
  x: 220,
  y: 380,
  section: 'Bar Lounge',
  capacity: 2,
  price: '$200 min'
}];
const secondFloorTables: TableData[] = [{
  id: '201',
  x: 100,
  y: 150,
  section: 'VIP Lounge',
  capacity: 6,
  price: '$800 min'
}, {
  id: '202',
  x: 160,
  y: 150,
  section: 'VIP Lounge',
  capacity: 6,
  price: '$800 min'
}, {
  id: '203',
  x: 220,
  y: 150,
  section: 'VIP Lounge',
  capacity: 8,
  price: '$1,000 min'
}, {
  id: '204',
  x: 280,
  y: 150,
  section: 'VIP Lounge',
  capacity: 8,
  price: '$1,000 min'
}, {
  id: '205',
  x: 100,
  y: 250,
  section: 'Mezzanine',
  capacity: 4,
  price: '$600 min'
}, {
  id: '206',
  x: 160,
  y: 250,
  section: 'Mezzanine',
  capacity: 4,
  price: '$600 min'
}, {
  id: '207',
  x: 220,
  y: 250,
  section: 'Mezzanine',
  capacity: 4,
  price: '$600 min'
}, {
  id: '208',
  x: 280,
  y: 250,
  section: 'Mezzanine',
  capacity: 4,
  price: '$600 min'
}, {
  id: '209',
  x: 400,
  y: 320,
  section: 'Second Bar',
  capacity: 2,
  price: '$300 min'
}, {
  id: '210',
  x: 450,
  y: 320,
  section: 'Second Bar',
  capacity: 2,
  price: '$300 min'
}];
const rooftopTables: TableData[] = [{
  id: 'R01',
  x: 120,
  y: 120,
  section: 'Outdoor Deck',
  capacity: 4,
  price: '$400 min'
}, {
  id: 'R02',
  x: 180,
  y: 120,
  section: 'Outdoor Deck',
  capacity: 4,
  price: '$400 min'
}, {
  id: 'R03',
  x: 240,
  y: 120,
  section: 'Outdoor Deck',
  capacity: 6,
  price: '$600 min'
}, {
  id: 'R04',
  x: 300,
  y: 120,
  section: 'Outdoor Deck',
  capacity: 6,
  price: '$600 min'
}, {
  id: 'R05',
  x: 120,
  y: 220,
  section: 'Patio Seating',
  capacity: 4,
  price: '$300 min'
}, {
  id: 'R06',
  x: 180,
  y: 220,
  section: 'Patio Seating',
  capacity: 4,
  price: '$300 min'
}, {
  id: 'R07',
  x: 240,
  y: 220,
  section: 'Patio Seating',
  capacity: 4,
  price: '$300 min'
}, {
  id: 'R08',
  x: 300,
  y: 220,
  section: 'Patio Seating',
  capacity: 4,
  price: '$300 min'
}, {
  id: 'R09',
  x: 420,
  y: 280,
  section: 'Rooftop Bar',
  capacity: 2,
  price: '$200 min'
}, {
  id: 'R10',
  x: 420,
  y: 330,
  section: 'Rooftop Bar',
  capacity: 2,
  price: '$200 min'
}];
export function FloorMap() {
  const [activeFloor, setActiveFloor] = useState<'first' | 'second' | 'rooftop'>('first');
  const [selectedTable, setSelectedTable] = useState<TableData | null>(null);
  const [hoveredTable, setHoveredTable] = useState<string | null>(null);
  const tables = activeFloor === 'first' ? firstFloorTables : activeFloor === 'second' ? secondFloorTables : rooftopTables;
  const [date, setDate] = useState('');
  const [time, setTime] = useState('');
  return <section className="w-full bg-[#050505] py-20 px-6 relative" data-id="element-140">
      <div className="max-w-7xl mx-auto" data-id="element-141">
        {/* Header */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} className="text-center mb-14" data-id="element-142">
          <span className="font-inter text-xs tracking-[0.4em] text-[#C9A84C]/70 uppercase" data-id="element-143">
            Interactive
          </span>
          <h2 className="font-montserrat font-black text-4xl sm:text-5xl tracking-widest text-white mt-2" data-id="element-144">
            FIND YOUR SPOT
          </h2>
          <div className="w-16 h-px bg-[#C9A84C]/50 mx-auto mt-4" data-id="element-145" />
          <p className="font-inter font-light text-white/50 text-sm mt-4" data-id="element-146">
            Click any table to make a reservation
          </p>
        </motion.div>

        {/* Floor Tabs */}
        <div className="flex justify-center gap-2 mb-8" data-id="element-147">
          {[{
          id: 'first',
          label: 'FIRST FLOOR'
        }, {
          id: 'second',
          label: 'SECOND FLOOR'
        }, {
          id: 'rooftop',
          label: 'ROOFTOP'
        }].map(floor => <button key={floor.id} onClick={() => {
          setActiveFloor(floor.id as any);
          setSelectedTable(null);
        }} className={`px-6 py-2.5 rounded-xl font-montserrat font-700 text-[11px] tracking-widest transition-all duration-300 ${activeFloor === floor.id ? 'bg-[#C9A84C] text-[#050505]' : 'border border-[#C9A84C]/20 text-[#C9A84C]/60 hover:border-[#C9A84C]/40 hover:text-[#C9A84C]'}`} data-id="element-148">
              {floor.label}
            </button>)}
        </div>

        {/* Map container */}
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.7
      }} className="relative rounded-2xl overflow-hidden border border-[#C9A84C]/20 mx-auto" style={{
        maxWidth: 600,
        background: '#0a0a0a'
      }} data-id="element-149">
          <svg viewBox="0 0 560 460" className="w-full" style={{
          display: 'block'
        }} data-id="element-150">
            {/* Background */}
            <rect width="560" height="460" fill="#0a0a0a" data-id="element-151" />

            {/* Outer walls */}
            <rect x="60" y="40" width="440" height="380" rx="8" fill="none" stroke="#C9A84C" strokeWidth="1.5" strokeOpacity="0.4" data-id="element-152" />

            {/* FIRST FLOOR SPECIFIC */}
            {activeFloor === 'first' && <>
                {/* Main entrance */}
                <rect x="230" y="400" width="100" height="20" rx="4" fill="#C9A84C" fillOpacity="0.15" stroke="#C9A84C" strokeWidth="1" strokeOpacity="0.6" data-id="element-153" />
                <text x="280" y="414" textAnchor="middle" fill="#C9A84C" fontSize="8" fontFamily="Montserrat" fontWeight="700" letterSpacing="2" opacity="0.8" data-id="element-154">
                  ENTRANCE
                </text>

                {/* Stage */}
                <rect x="80" y="55" width="400" height="70" rx="6" fill="#C9A84C" fillOpacity="0.08" stroke="#C9A84C" strokeWidth="1" strokeOpacity="0.5" data-id="element-155" />
                <text x="280" y="96" textAnchor="middle" fill="#C9A84C" fontSize="10" fontFamily="Montserrat" fontWeight="800" letterSpacing="3" opacity="0.9" data-id="element-156">
                  STAGE
                </text>

                {/* Dance floor */}
                <rect x="130" y="230" width="220" height="120" rx="6" fill="#C9A84C" fillOpacity="0.04" stroke="#C9A84C" strokeWidth="1" strokeOpacity="0.25" strokeDasharray="4,4" data-id="element-157" />
                <text x="240" y="340" textAnchor="middle" fill="#C9A84C" fontSize="9" fontFamily="Montserrat" fontWeight="700" letterSpacing="2" opacity="0.5" data-id="element-158">
                  DANCE FLOOR
                </text>

                {/* Bar */}
                <rect x="370" y="240" width="110" height="30" rx="4" fill="#C9A84C" fillOpacity="0.12" stroke="#C9A84C" strokeWidth="1" strokeOpacity="0.5" data-id="element-159" />
                <text x="425" y="285" textAnchor="middle" fill="#C9A84C" fontSize="8" fontFamily="Montserrat" fontWeight="700" letterSpacing="2" opacity="0.9" data-id="element-160">
                  BAR
                </text>

                {/* VIP Section area */}
                <rect x="80" y="155" width="280" height="60" rx="4" fill="#C9A84C" fillOpacity="0.05" stroke="#C9A84C" strokeWidth="0.75" strokeOpacity="0.3" strokeDasharray="3,3" data-id="element-161" />
                <text x="90" y="148" fill="#C9A84C" fontSize="7" fontFamily="Montserrat" fontWeight="700" letterSpacing="2" opacity="0.6" data-id="element-162">
                  VIP SECTION
                </text>

                {/* Skybox */}
                <rect x="380" y="110" width="100" height="110" rx="4" fill="#C9A84C" fillOpacity="0.07" stroke="#C9A84C" strokeWidth="1" strokeOpacity="0.4" data-id="element-163" />
                <text x="430" y="130" textAnchor="middle" fill="#C9A84C" fontSize="7" fontFamily="Montserrat" fontWeight="700" letterSpacing="1.5" opacity="0.8" data-id="element-164">
                  SKYBOX
                </text>

                {/* Bar lounge area */}
                <rect x="80" y="355" width="200" height="50" rx="4" fill="#C9A84C" fillOpacity="0.05" stroke="#C9A84C" strokeWidth="0.75" strokeOpacity="0.3" strokeDasharray="3,3" data-id="element-165" />
                <text x="90" y="415" fill="#C9A84C" fontSize="7" fontFamily="Montserrat" fontWeight="700" letterSpacing="2" opacity="0.6" data-id="element-166">
                  BAR LOUNGE
                </text>
              </>}

            {/* SECOND FLOOR SPECIFIC */}
            {activeFloor === 'second' && <>
                {/* Stairs */}
                <rect x="420" y="60" width="60" height="100" rx="4" fill="#C9A84C" fillOpacity="0.05" stroke="#C9A84C" strokeWidth="0.5" strokeOpacity="0.3" data-id="element-167" />
                <path d="M420 80h60 M420 100h60 M420 120h60 M420 140h60" stroke="#C9A84C" strokeWidth="0.5" strokeOpacity="0.2" data-id="element-168" />
                <text x="450" y="175" textAnchor="middle" fill="#C9A84C" fontSize="6" fontFamily="Montserrat" fontWeight="700" opacity="0.5" data-id="element-169">
                  STAIRS
                </text>

                {/* DJ Booth */}
                <rect x="390" y="190" width="100" height="40" rx="4" fill="#C9A84C" fillOpacity="0.1" stroke="#C9A84C" strokeWidth="1" strokeOpacity="0.6" data-id="element-170" />
                <text x="440" y="215" textAnchor="middle" fill="#C9A84C" fontSize="8" fontFamily="Montserrat" fontWeight="800" letterSpacing="1" opacity="0.9" data-id="element-171">
                  DJ BOOTH
                </text>

                {/* VIP Lounge */}
                <rect x="80" y="80" width="300" height="120" rx="6" fill="#C9A84C" fillOpacity="0.08" stroke="#C9A84C" strokeWidth="1" strokeOpacity="0.5" data-id="element-172" />
                {/* Sofa shapes */}
                <rect x="90" y="90" width="60" height="15" rx="2" fill="#C9A84C" fillOpacity="0.1" data-id="element-173" />
                <rect x="310" y="90" width="60" height="15" rx="2" fill="#C9A84C" fillOpacity="0.1" data-id="element-174" />
                <text x="230" y="110" textAnchor="middle" fill="#C9A84C" fontSize="10" fontFamily="Montserrat" fontWeight="800" letterSpacing="3" opacity="0.9" data-id="element-175">
                  VIP LOUNGE
                </text>

                {/* Mezzanine Railing */}
                <line x1="80" x2="380" y1="210" y2="210" stroke="#C9A84C" strokeWidth="1" strokeOpacity="0.4" strokeDasharray="2,2" data-id="element-176" />

                {/* Mezzanine */}
                <rect x="80" y="220" width="300" height="100" rx="6" fill="#C9A84C" fillOpacity="0.04" stroke="#C9A84C" strokeWidth="1" strokeOpacity="0.25" strokeDasharray="4,4" data-id="element-177" />
                <text x="230" y="305" textAnchor="middle" fill="#C9A84C" fontSize="9" fontFamily="Montserrat" fontWeight="700" letterSpacing="2" opacity="0.5" data-id="element-178">
                  MEZZANINE
                </text>

                {/* Second Bar */}
                <rect x="390" y="280" width="100" height="80" rx="4" fill="#C9A84C" fillOpacity="0.12" stroke="#C9A84C" strokeWidth="1" strokeOpacity="0.5" data-id="element-179" />
                <text x="440" y="355" textAnchor="middle" fill="#C9A84C" fontSize="8" fontFamily="Montserrat" fontWeight="700" letterSpacing="2" opacity="0.9" data-id="element-180">
                  BAR
                </text>

                {/* Restrooms */}
                <rect x="80" y="340" width="120" height="40" rx="4" fill="#C9A84C" fillOpacity="0.03" stroke="#C9A84C" strokeWidth="0.5" strokeOpacity="0.2" data-id="element-181" />
                <text x="140" y="365" textAnchor="middle" fill="#C9A84C" fontSize="6" fontFamily="Montserrat" fontWeight="700" opacity="0.4" data-id="element-182">
                  RESTROOMS
                </text>
              </>}

            {/* ROOFTOP SPECIFIC */}
            {activeFloor === 'rooftop' && <>
                {/* View Indicator */}
                <rect x="60" y="390" width="440" height="30" rx="4" fill="#C9A84C" fillOpacity="0.05" data-id="element-183" />
                <text x="280" y="410" textAnchor="middle" fill="#C9A84C" fontSize="7" fontFamily="Montserrat" fontWeight="800" letterSpacing="4" opacity="0.6" data-id="element-184">
                  U ST VIEW
                </text>

                {/* Outdoor Deck */}
                <rect x="80" y="80" width="350" height="100" rx="6" fill="#C9A84C" fillOpacity="0.08" stroke="#C9A84C" strokeWidth="1" strokeOpacity="0.5" data-id="element-185" />
                {/* Umbrella icons */}
                <circle cx="120" cy="120" r="15" fill="#C9A84C" fillOpacity="0.1" stroke="#C9A84C" strokeWidth="0.5" strokeOpacity="0.3" data-id="element-186" />
                <circle cx="300" cy="120" r="15" fill="#C9A84C" fillOpacity="0.1" stroke="#C9A84C" strokeWidth="0.5" strokeOpacity="0.3" data-id="element-187" />
                <text x="255" y="100" textAnchor="middle" fill="#C9A84C" fontSize="10" fontFamily="Montserrat" fontWeight="800" letterSpacing="3" opacity="0.9" data-id="element-188">
                  OUTDOOR DECK
                </text>

                {/* Planters */}
                <rect x="80" y="185" width="350" height="10" rx="2" fill="#C9A84C" fillOpacity="0.15" data-id="element-189" />
                <text x="255" y="193" textAnchor="middle" fill="#C9A84C" fontSize="5" fontFamily="Montserrat" fontWeight="700" opacity="0.5" data-id="element-190">
                  PLANTERS
                </text>

                {/* Patio Seating */}
                <rect x="80" y="200" width="300" height="120" rx="6" fill="#C9A84C" fillOpacity="0.04" stroke="#C9A84C" strokeWidth="1" strokeOpacity="0.25" strokeDasharray="4,4" data-id="element-191" />
                <text x="230" y="305" textAnchor="middle" fill="#C9A84C" fontSize="9" fontFamily="Montserrat" fontWeight="700" letterSpacing="2" opacity="0.5" data-id="element-192">
                  PATIO SEATING
                </text>

                {/* Rooftop Bar */}
                <rect x="390" y="260" width="80" height="120" rx="4" fill="#C9A84C" fillOpacity="0.12" stroke="#C9A84C" strokeWidth="1" strokeOpacity="0.5" data-id="element-193" />
                <text x="455" y="325" textAnchor="middle" fill="#C9A84C" fontSize="8" fontFamily="Montserrat" fontWeight="700" letterSpacing="2" opacity="0.9" transform="rotate(-90, 455, 325)" data-id="element-194">
                  ROOFTOP BAR
                </text>

                {/* Fire Pit Area */}
                <rect x="340" y="200" width="40" height="40" rx="20" fill="#C9A84C" fillOpacity="0.08" stroke="#C9A84C" strokeWidth="1" strokeOpacity="0.4" data-id="element-195" />
                <text x="360" y="255" textAnchor="middle" fill="#C9A84C" fontSize="6" fontFamily="Montserrat" fontWeight="700" opacity="0.5" data-id="element-196">
                  FIRE PIT
                </text>
              </>}

            {/* Table hotspots */}
            {tables.map(table => <g key={table.id} data-id="element-197">
                {/* Pulse ring */}
                <circle cx={table.x} cy={table.y} r="14" fill="none" stroke="#C9A84C" strokeWidth="1" strokeOpacity={hoveredTable === table.id ? 0.6 : 0.2} className="table-hotspot" data-id="element-198" />
                {/* Main circle */}
                <circle cx={table.x} cy={table.y} r="9" fill={hoveredTable === table.id ? '#C9A84C' : '#C9A84C33'} stroke="#C9A84C" strokeWidth="1.5" strokeOpacity={hoveredTable === table.id ? 1 : 0.7} style={{
              cursor: 'pointer',
              transition: 'all 0.2s'
            }} onMouseEnter={() => setHoveredTable(table.id)} onMouseLeave={() => setHoveredTable(null)} onClick={() => setSelectedTable(table)} data-id="element-199" />
                {/* Table number */}
                <text x={table.x} y={table.y + 4} textAnchor="middle" fill={hoveredTable === table.id ? '#050505' : '#C9A84C'} fontSize="7" fontFamily="Montserrat" fontWeight="800" style={{
              cursor: 'pointer',
              pointerEvents: 'none'
            }} data-id="element-200">
                  {table.id}
                </text>

                {/* Tooltip */}
                {hoveredTable === table.id && <g data-id="element-201">
                    <rect x={table.x - 40} y={table.y - 38} width="80" height="26" rx="4" fill="#050505" stroke="#C9A84C" strokeWidth="0.75" strokeOpacity="0.6" data-id="element-202" />
                    <text x={table.x} y={table.y - 28} textAnchor="middle" fill="#C9A84C" fontSize="7" fontFamily="Montserrat" fontWeight="700" data-id="element-203">
                      TABLE {table.id}
                    </text>
                    <text x={table.x} y={table.y - 18} textAnchor="middle" fill="rgba(255,255,255,0.6)" fontSize="6.5" fontFamily="Inter" data-id="element-204">
                      {table.capacity} guests
                    </text>
                  </g>}
              </g>)}
          </svg>

          {/* Legend */}
          <div className="flex flex-wrap gap-4 p-4 border-t border-[#C9A84C]/10" data-id="element-205">
            <div className="flex items-center gap-2" data-id="element-206">
              <div className="w-3 h-3 rounded-full bg-[#C9A84C]/30 border border-[#C9A84C]" data-id="element-207" />
              <span className="font-inter text-xs text-white/50" data-id="element-208">
                Available Table
              </span>
            </div>
            <div className="flex items-center gap-2" data-id="element-209">
              <div className="w-3 h-3 rounded-full bg-[#C9A84C]" data-id="element-210" />
              <span className="font-inter text-xs text-white/50" data-id="element-211">Selected</span>
            </div>
            <div className="ml-auto font-inter text-xs text-white/30" data-id="element-212">
              {tables.length} tables available
            </div>
          </div>
        </motion.div>
      </div>

      {/* Side Panel Drawer */}
      <AnimatePresence data-id="element-213">
        {selectedTable && <>
            {/* Overlay */}
            <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} transition={{
          duration: 0.3
        }} className="fixed inset-0 z-40 bg-black/60" onClick={() => setSelectedTable(null)} data-id="element-214" />

            {/* Drawer */}
            <motion.div initial={{
          x: '100%'
        }} animate={{
          x: 0
        }} exit={{
          x: '100%'
        }} transition={{
          type: 'spring',
          stiffness: 300,
          damping: 35
        }} className="fixed top-0 right-0 bottom-0 z-50 flex flex-col overflow-y-auto" style={{
          width: 'min(380px, 100vw)',
          background: 'rgba(5,5,5,0.95)',
          backdropFilter: 'blur(24px)',
          WebkitBackdropFilter: 'blur(24px)',
          borderLeft: '1px solid rgba(201,168,76,0.3)'
        }} data-id="element-215">
              {/* Drawer header */}
              <div className="flex items-center justify-between p-6 border-b border-[#C9A84C]/15" data-id="element-216">
                <div data-id="element-217">
                  <p className="font-montserrat text-[10px] tracking-widest text-[#C9A84C]/70 mb-1" data-id="element-218">
                    RESERVATION
                  </p>
                  <h3 className="font-montserrat font-black text-xl tracking-widest text-white" data-id="element-219">
                    TABLE {selectedTable.id}
                  </h3>
                </div>
                <button onClick={() => setSelectedTable(null)} className="w-9 h-9 flex items-center justify-center rounded-full border border-white/20 text-white/60 hover:border-[#C9A84C]/60 hover:text-[#C9A84C] transition-all" aria-label="Close drawer" data-id="element-220">
                  <XIcon size={16} data-id="element-221" />
                </button>
              </div>

              {/* Table info */}
              <div className="p-6 border-b border-[#C9A84C]/10" data-id="element-222">
                <div className="grid grid-cols-2 gap-3" data-id="element-223">
                  <div className="bg-white/5 rounded-xl p-3 border border-[#C9A84C]/10" data-id="element-224">
                    <p className="font-montserrat text-[9px] tracking-widest text-white/40 mb-1" data-id="element-225">
                      SECTION
                    </p>
                    <p className="font-montserrat font-700 text-sm text-white" data-id="element-226">
                      {selectedTable.section}
                    </p>
                  </div>
                  <div className="bg-white/5 rounded-xl p-3 border border-[#C9A84C]/10" data-id="element-227">
                    <div className="flex items-center gap-1.5 mb-1" data-id="element-228">
                      <UsersIcon size={10} className="text-[#C9A84C]" data-id="element-229" />
                      <p className="font-montserrat text-[9px] tracking-widest text-white/40" data-id="element-230">
                        CAPACITY
                      </p>
                    </div>
                    <p className="font-montserrat font-700 text-sm text-white" data-id="element-231">
                      Up to {selectedTable.capacity}
                    </p>
                  </div>
                  <div className="col-span-2 bg-[#C9A84C]/10 rounded-xl p-3 border border-[#C9A84C]/20" data-id="element-232">
                    <p className="font-montserrat text-[9px] tracking-widest text-white/40 mb-1" data-id="element-233">
                      MINIMUM SPEND
                    </p>
                    <p className="font-montserrat font-black text-lg text-[#C9A84C]" data-id="element-234">
                      {selectedTable.price}
                    </p>
                  </div>
                </div>
              </div>

              {/* Form */}
              <div className="p-6 flex-1" data-id="element-235">
                <p className="font-montserrat text-[10px] tracking-widest text-white/40 mb-4" data-id="element-236">
                  SELECT DATE & TIME
                </p>

                <div className="space-y-3 mb-6" data-id="element-237">
                  <div className="relative" data-id="element-238">
                    <CalendarIcon size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#C9A84C] pointer-events-none" data-id="element-239" />
                    <input type="date" value={date} onChange={e => setDate(e.target.value)} className="w-full bg-white/5 border border-[#C9A84C]/20 rounded-lg pl-9 pr-3 py-3 text-white/80 text-sm font-inter focus:outline-none focus:border-[#C9A84C]/60 transition-colors [color-scheme:dark]" data-id="element-240" />
                  </div>

                  <div className="relative" data-id="element-241">
                    <ClockIcon size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#C9A84C] pointer-events-none z-10" data-id="element-242" />
                    <select value={time} onChange={e => setTime(e.target.value)} className="w-full bg-white/5 border border-[#C9A84C]/20 rounded-lg pl-9 pr-3 py-3 text-white/80 text-sm font-inter focus:outline-none focus:border-[#C9A84C]/60 transition-colors appearance-none cursor-pointer" style={{
                  background: 'rgba(5,5,5,0.9)'
                }} data-id="element-243">
                      <option value="" style={{
                    background: '#050505'
                  }} data-id="element-244">
                        Select arrival time
                      </option>
                      {['9:00 PM', '9:30 PM', '10:00 PM', '10:30 PM', '11:00 PM', '11:30 PM', '12:00 AM', '12:30 AM', '1:00 AM'].map(t => <option key={t} value={t} style={{
                    background: '#050505'
                  }} data-id="element-245">
                          {t}
                        </option>)}
                    </select>
                  </div>
                </div>

                <div className="bg-white/5 rounded-xl p-4 border border-[#C9A84C]/10 mb-6" data-id="element-246">
                  <p className="font-inter text-xs text-white/50 leading-relaxed" data-id="element-247">
                    A $200 deposit is required to confirm your reservation.
                    Remaining balance applied to minimum spend.
                  </p>
                </div>

                <button className="w-full font-montserrat font-700 text-xs tracking-widest bg-[#C9A84C] text-[#050505] py-4 rounded-xl hover:bg-[#E8C97A] transition-colors duration-300" data-id="element-248">
                  CONFIRM RESERVATION
                </button>
              </div>
            </motion.div>
          </>}
      </AnimatePresence>
    </section>;
}